import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedConversation } from "../../Slices/ChatSlice";
import useGetConversations from "../../hooks/useGetConversations";
import userProfile from "../../../assets/Images/user.png";
import userProfileLight from "../../../assets/Images/user2.png";

import groupProfile from "../../../assets/Images/group.png";
import groupProfileLight from "../../../assets/Images/users2.png";

const Conversations = ({ onlineUsers }) => {
  const dispatch = useDispatch();
  const selectedConversationType = useSelector(
    (state) => state.conversation.conversationType
  );
  const selectedConversation = useSelector(
    (state) => state.conversation.selectedConversation
  );
  const isChatModalOpen = useSelector((state) => state.conversation.isChatModalOpen);

  const [conversationType, setConversationType] = useState(
    selectedConversationType ? selectedConversationType : "simple"
  );

  // Single source of truth for both tabs. This component used to run a second
  // fetch + merge/reorder layer of its own alongside this hook, reconciled by
  // three effects and four refs. That layer's pagination never actually ran
  // (the request ignored the page argument, the response carries no
  // totalPages, and its scroll handler was never attached), and its only live
  // behaviour — moving the conversation you just messaged to the top — is now
  // done directly in the store by `promoteConversation`, so the whole thing is
  // gone in favour of reading Redux straight.
  const { loading: listLoading } = useGetConversations(false, conversationType);

  const conversationsData = useSelector(
    (state) => state.conversation.withMessagesConversations || []
  );
  const groupsConversationsData = useSelector(
    (state) => state.conversation.withMessagesGroups || []
  );

  const conversationsListRef = useRef(null);
  const conversationNodeByValueRef = useRef(new Map());

  const handleChatSelection = (conversation) => {
    dispatch(setSelectedConversation({ conversation, type: conversationType }));
  };

  // Calculate total unread counts
  const totalUnreadCounts = useMemo(
    () => ({
      simple: conversationsData.reduce(
        (total, conversation) => total + (conversation?.unreadCount || 0),
        0
      ),
      group: groupsConversationsData.reduce(
        (total, conversation) => total + (conversation?.unreadCount || 0),
        0
      ),
    }),
    [conversationsData, groupsConversationsData]
  );

  // Filter conversations based on the selected type
  const filteredConversations =
    conversationType === "group" ? groupsConversationsData : conversationsData;

  const isInitialListLoading =
    listLoading && filteredConversations.length === 0;

  const scrollSelectedConversationIntoView = useCallback(() => {
    const container = conversationsListRef.current;
    const selectedValue = selectedConversation?.value;
    if (!container || selectedValue == null) return;

    const node = conversationNodeByValueRef.current.get(selectedValue);
    if (!node) return;

    const padding = 16;
    const nodeTop = node.offsetTop;
    const nodeBottom = nodeTop + node.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    if (nodeTop < viewTop + padding) {
      container.scrollTop = Math.max(0, nodeTop - padding);
      return;
    }

    if (nodeBottom > viewBottom - padding) {
      container.scrollTop = Math.max(
        0,
        nodeBottom - container.clientHeight + padding,
      );
    }
  }, [selectedConversation?.value]);

  // When chat modal opens (or list mounts), keep the selected conversation visible.
  useEffect(() => {
    if (!isChatModalOpen) return;
    if (!selectedConversation?.value) return;

    const rafId = requestAnimationFrame(() => {
      scrollSelectedConversationIntoView();
    });

    return () => cancelAnimationFrame(rafId);
  }, [
    isChatModalOpen,
    selectedConversation?.value,
    filteredConversations?.length,
    scrollSelectedConversationIntoView,
  ]);

  return (
    <div className="py-2 flex flex-1 min-h-0 flex-col z-10">
      {/* Toggle Button - Styled to match the image */}
      <div className="flex shrink-0 justify-center drop-shadow-md mb-4 px-8">
        <div className="flex w-full rounded-full bg-blue-50 p-1 mx-10">
          <button
            onClick={() => {
              setConversationType("simple");
              dispatch(
                setSelectedConversation({
                  conversation: null,
                  type: "simple",
                })
              );
            }}
            className={`relative flex-1 py-1 text-sm  rounded-full transition-all duration-200 text-xs ${
              conversationType === "simple"
                ? "bg-sideNavbg text-white shadow-sm"
                : "text-gray-500"
            }`}
          >
            Personal
            {conversationType === "group" && totalUnreadCounts.simple > 0 && (
              <span className="absolute -top-2 -left-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {totalUnreadCounts.simple > 99
                  ? "99+"
                  : totalUnreadCounts.simple}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setConversationType("group");
              dispatch(
                setSelectedConversation({
                  conversation: null,
                  type: "group",
                })
              );
            }}
            className={`relative flex-1 py-1 text-sm  rounded-full transition-all duration-200 ${
              conversationType === "group"
                ? "bg-sideNavbg text-white shadow-sm"
                : "text-gray-500"
            }`}
            style={{ fontFamily: "'Mulish', 'Noto Color Emoji', sans-serif" }}
          >
            Groups
            {conversationType === "simple" && totalUnreadCounts.group > 0 && (
              <span className="absolute -top-2 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {totalUnreadCounts.group > 99 ? "99+" : totalUnreadCounts.group}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Conversations List */}
      <div
        className="px-1 space-y-2 mt-4 flex-1 min-h-0 overflow-auto"
        ref={conversationsListRef}
      >
        {isInitialListLoading ? (
          // Skeleton loading state
          Array(6)
            .fill(0)
            .map((_, index) => (
              <div
                key={index}
                className="flex items-center p-3 rounded-xl bg-gray-100 animate-pulse"
              >
                <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                <div className="ml-3 space-y-1.5 flex-1">
                  <div className="h-3.5 bg-gray-300 rounded w-24"></div>
                  <div className="h-2.5 bg-gray-300 rounded w-32"></div>
                </div>
              </div>
            ))
        ) : filteredConversations?.length > 0 ? (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.value}
              ref={(node) => {
                const key = conversation?.value;
                if (key == null) return;
                if (node) conversationNodeByValueRef.current.set(key, node);
                else conversationNodeByValueRef.current.delete(key);
              }}
              className={`chat-conversation-row flex items-center p-3 rounded-xl cursor-pointer transition-colors duration-200 relative drop-shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brandAccent ${
                selectedConversation?.value === conversation.value
                  ? "bg-sideNavbg text-white"
                  : "bg-blue-50 hover:bg-blue-100 text-gray-800"
              }`}
              onClick={() => handleChatSelection(conversation)}
              // Keyboard operability: these rows are plain divs, so without an
              // explicit role/tabIndex/key handler the conversation list can
              // only be opened with a mouse. Enter and Space match native
              // button behaviour.
              role="button"
              tabIndex={0}
              aria-current={
                selectedConversation?.value === conversation.value
                  ? "true"
                  : undefined
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleChatSelection(conversation);
                }
              }}
            >
              {/* Profile Image */}
              <div className="relative">
                <img
                  src={
                    conversation?.profilePicture
                      ? `data:image/png;base64,${conversation?.profilePicture}`
                      : conversationType === "group" &&
                        selectedConversation?.value === conversation.value
                      ? groupProfileLight
                      : conversationType === "group" &&
                        selectedConversation?.value !== conversation.value
                      ? groupProfile
                      : conversationType === "simple" &&
                        selectedConversation?.value === conversation.value
                      ? userProfileLight
                      : userProfile
                  }
                  alt={`${conversation.label}'s profile`}
                  className="w-10 h-10 rounded-full object-cover"
                />

                {/* Online Status Indicator */}
                {conversationType !== "group" && (
                  <span
                    className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full  ${
                      onlineUsers[conversation.value] === true ||
                      onlineUsers[conversation.value] === "true"
                        ? "bg-green-500"
                        : "bg-gray-400"
                    }`}
                  ></span>
                )}
              </div>

              {/* Conversation Details */}
              <div className="ml-3 flex-1 overflow-hidden ">
                <p
                  className={`font-bold  text-sm line-clamp-1 ${
                    selectedConversation?.value === conversation.value
                      ? "text-white"
                      : "text-gray-800"
                  }`}
                  style={{ fontFamily: "'Mulish', 'Noto Color Emoji', sans-serif" }}
                  title={conversation?.label}
                >
                  {conversation?.label}
                </p>
                <p
                  className={`text-xs truncate mt-[1px] ${
                    selectedConversation?.value === conversation.value
                      ? "text-blue-100"
                      : "text-gray-500"
                  }`}
                  style={{ fontFamily: "'Mulish', 'Noto Color Emoji', sans-serif" }}
                >
                  {conversation?.email}
                </p>
              </div>

              {/* Unread Count Badge */}
              {conversation?.unreadCount > 0 &&
                conversation?.value !== selectedConversation?.value && (
                  <div className="absolute top-5 right-4 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {conversation?.unreadCount > 99
                      ? "99+"
                      : conversation?.unreadCount}
                  </div>
                )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            No recent conversations
          </div>
        )}

        {/* End-of-list footer. Its old guards were all permanently true once
            the personal list had loaded (the removed pager never advanced past
            page 1), so this renders in exactly the same cases as before. */}
        {conversationType === "simple" && filteredConversations.length > 0 && (
          <div className="text-center py-4 text-gray-400 text-sm">
            No more conversations
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversations;
